"use client";

import { use } from "react";
import Link from "next/link";
import useSWR from "swr";
import axios from "axios";
import { LuArrowLeft, LuBookOpen } from "react-icons/lu";
import {
  ArticleCard,
  ArticleCardSkeleton,
  type ArticleCardData,
} from "@/components/articles/ArticleCard";

const API = process.env.NEXT_PUBLIC_API_BASE_URL;
const fetcher = (url: string) => axios.get(url).then((r) => r.data);

export default function AuthorPage({
  params,
}: {
  params: Promise<{ "author-slug": string }>;
}) {
  const { "author-slug": authorSlug } = use(params);
  const { data, isLoading } = useSWR(
    `${API}/articles/author/${authorSlug}`,
    fetcher,
    { revalidateOnFocus: false },
  );

  const articles: ArticleCardData[] = data?.articles ?? [];
  const author = data?.author;
  const servicesOffered: string[] = Array.isArray(author?.servicesOffered)
    ? author.servicesOffered
    : [];

  return (
    <div className="max-w-5xl mx-auto px-5 py-10 space-y-8">
      <div>
        <Link
          href="/blog"
          className="inline-flex items-center gap-1 text-sm text-indigo-600 dark:text-indigo-400 hover:underline mb-4"
        >
          <LuArrowLeft className="w-4 h-4" /> All blog posts
        </Link>
        {author && (
          <div className="flex items-start gap-4 mt-2">
            {author.avatar ? (
              // eslint-disable-next-line @next/next/no-img-element
              <img
                src={author.avatar}
                alt={author.displayName}
                className="w-16 h-16 rounded-full object-cover border-2 border-indigo-200 dark:border-indigo-500/40 shrink-0"
              />
            ) : (
              <div className="w-16 h-16 rounded-full bg-indigo-100 dark:bg-indigo-500/20 flex items-center justify-center text-indigo-600 dark:text-indigo-400 font-bold text-lg border-2 border-indigo-200 dark:border-indigo-500/30 shrink-0">
                {author.displayName
                  .split(" ")
                  .map((n: string) => n[0])
                  .join("")
                  .toUpperCase()
                  .slice(0, 2)}
              </div>
            )}
            <div className="flex-1 min-w-0">
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                {author.displayName}
              </h1>
              {author.bio && (
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 max-w-xl">
                  {author.bio}
                </p>
              )}
              {servicesOffered.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-3">
                  {servicesOffered.map((s: string) => (
                    <span
                      key={s}
                      className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-50 dark:bg-indigo-500/10 text-indigo-700 dark:text-indigo-300 border border-indigo-100 dark:border-indigo-500/20"
                    >
                      {s}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
        {!author && !isLoading && (
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            Author
          </h1>
        )}
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-3">
          {articles.length} blog post{articles.length !== 1 ? "s" : ""}
        </p>
      </div>

      {isLoading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3].map((i) => (
            <ArticleCardSkeleton key={i} />
          ))}
        </div>
      ) : articles.length === 0 ? (
        <div className="text-center py-16 text-gray-500 dark:text-gray-400">
          <LuBookOpen className="w-12 h-12 mx-auto mb-3 opacity-30" />
          <p>No blog posts by this author yet.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {articles.map((a) => (
            <ArticleCard key={a.id} article={a} />
          ))}
        </div>
      )}
    </div>
  );
}
